import React, { ReactElement } from 'react';
import { Button, Card, HelpTooltip } from '@odin-labs/components';
import { useGetMyPinQuery } from 'apollo/generated/client-operations';
import { ExclamationTriangleIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { getPrettyFormattedUtcDate } from 'utils/dates';
import { SetPinDrawer } from './modals/setPinDrawer/SetPinDrawer';

const formatCountdown = (msRemaining: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds} sec`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')} min`;
};

export function Security(): ReactElement {
  const { data, loading, error, refetch } = useGetMyPinQuery({
    fetchPolicy: 'no-cache',
  });
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const openDrawer = React.useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = React.useCallback(() => setDrawerOpen(false), []);
  const [now, setNow] = React.useState(() => Date.now());

  const pin = data?.getCurrentSession?.user?.pin;
  const lockedUntilMs = pin?.lockedUntil
    ? new Date(pin.lockedUntil).getTime()
    : null;
  const isLocked = lockedUntilMs != null && lockedUntilMs > now;

  // Tick `now` every second while locked so the countdown updates live and
  // `isLocked` flips back to false automatically the moment the lockout
  // expires (no refetch needed — `lockedUntil` is an absolute timestamp).
  React.useEffect((): undefined | (() => void) => {
    if (!isLocked) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isLocked]);

  if (loading) return <LoadingError loading />;
  if (error) return <LoadingError error={error} />;

  const hasPin = !!pin;
  const subtitle = ((): string => {
    if (!hasPin) return 'No PIN configured';
    if (isLocked) {
      return `Too many failed attempts. Unlocks in ${formatCountdown(
        lockedUntilMs - now,
      )}`;
    }
    return `Last set ${getPrettyFormattedUtcDate(pin.pinSetAt)}`;
  })();
  const buttonText = ((): string => {
    if (!hasPin) return 'Set PIN';
    if (isLocked) return 'Unlock PIN';
    return 'Change PIN';
  })();

  const header = (
    <div className="odin-flex odin-flex-wrap odin-items-center odin-justify-between odin-gap-4">
      <div>
        <h4 className="odin-text-1.5sm odin-font-medium odin-text-gray-900 odin-mb-0 odin-flex odin-items-center odin-gap-1.5">
          Device PIN
          <HelpTooltip ariaLabel="What is a Device PIN?">
            Used to access shared field devices like hand-held scanners.
          </HelpTooltip>
        </h4>
        <p className="odin-text-sm odin-text-gray-500 odin-mb-0 odin-mt-0.5 odin-flex odin-items-center odin-gap-1.5">
          {isLocked ? (
            <ExclamationTriangleIcon
              className="odin-w-4 odin-h-4 odin-text-amber-500"
              aria-hidden="true"
            />
          ) : null}
          {subtitle}
        </p>
      </div>
      <Button
        theme={hasPin && !isLocked ? 'secondary' : 'primary'}
        text={buttonText}
        size={['base', 'md:sm']}
        onClick={openDrawer}
      />
    </div>
  );

  return (
    <>
      <Card headerContent={header} />
      <SetPinDrawer
        isOpen={isDrawerOpen}
        closeDrawer={closeDrawer}
        hasExistingPin={hasPin}
        isLocked={isLocked}
        onPinSet={refetch}
      />
    </>
  );
}
